import {Injectable} from "@angular/core";
import {CountryModel} from "../types/country";
import {RegionsByCountryModel, RegionModel, regionsByCountry} from "../types/region";
import {GeoRegionModel} from "../types/geo-region";
import {ContactLocaleModel} from "../types/contact-locale";
import {HttpClient} from "@angular/common/http";
import {BehaviorSubject, Observable, Subject} from "rxjs";
import {map} from "rxjs/operators";
import {AppSettingsModel} from "./app-settings.model";
import {AuthDetailModel} from "./auth-detail.model";
import {AppSettingsResponseModel} from "./app-settings-response.model";
import {ContactCountryModel} from "../types/contact-country";
import {ContactLanguageModel} from "../types/contact-language";
import {FeatureFlagModel} from "../types/feature-flag";


@Injectable({providedIn: "root"})
export class AppSettingsService {
  private settingsSubject: Subject<AppSettingsModel> = new BehaviorSubject<AppSettingsModel>(new AppSettingsModel());
  private userSubject: Subject<AuthDetailModel> = new BehaviorSubject<AuthDetailModel>(new AuthDetailModel());
  private contactLocalesSubject: Subject<ContactLocaleModel[]> = new BehaviorSubject<ContactLocaleModel[]>([]);
  private contactCountriesSubject: Subject<ContactCountryModel[]> = new BehaviorSubject<ContactCountryModel[]>([]);
  private contactLanguagesSubject: Subject<ContactLanguageModel[]> = new BehaviorSubject<ContactLanguageModel[]>([]);
  private countriesSubject: Subject<CountryModel[]> = new BehaviorSubject<CountryModel[]>([]);
  private geoRegionsSubject: Subject<GeoRegionModel[]> = new BehaviorSubject<GeoRegionModel[]>([]);
  private regionsSubject: Subject<RegionModel[]> = new BehaviorSubject<RegionModel[]>([]);
  private regionsByCountrySubject: Subject<RegionsByCountryModel> = new BehaviorSubject<RegionsByCountryModel>({});
  private refreshBrowserContentSubject: Subject<any> = new BehaviorSubject<any>(null);
  private featureFlagsSubject: Subject<FeatureFlagModel[]> = new BehaviorSubject<FeatureFlagModel[]>([]);

  /**
   * Get env.settings
   * @type {Observable<AppSettingsModel>}
   */
  settings: Observable<AppSettingsModel> = this.settingsSubject.asObservable();

  /**
   * Get logged-in user
   * @type {Observable<AuthDetailModel>}
   */
  user: Observable<AuthDetailModel> = this.userSubject.asObservable();

  /**
   * Get list of supported countries (for countries dropdown)
   * @type {Observable<CountryModel[]>}
   */
  countries: Observable<CountryModel[]> = this.countriesSubject.asObservable();

  /**
   * Get list of supported geo-regions (for geo-regions dropdown)
   * @type {Observable<CountryModel[]>}
   */
  geoRegions: Observable<GeoRegionModel[]> = this.geoRegionsSubject.asObservable();

  /**
   * Get list of supported geo-regions (for geo-regions dropdown)
   * @type {Observable<CountryModel[]>}
   */
  regions: Observable<RegionModel[]> = this.regionsSubject.asObservable();
  regionsByCountry: Observable<RegionsByCountryModel> = this.regionsByCountrySubject.asObservable();

  /**
   * Get list of supported contact-locales (for contact-locale dropdown)
   * @type {Observable<ContactLocaleModel[]>}
   */
  contactLocales: Observable<ContactLocaleModel[]> = this.contactLocalesSubject.asObservable();

  /**
   * Get list of supported contact-country (for contact-country dropdown)
   * @type {Observable<ContactCountryModel[]>}
   */
   contactCountries: Observable<ContactCountryModel[]> = this.contactCountriesSubject.asObservable();

  /**
   * Get list of supported contact-language (for contact-language dropdown)
   * @type {Observable<ContactLanguageModel[]>}
   */
   contactLanguages: Observable<ContactLanguageModel[]> = this.contactLanguagesSubject.asObservable();

   refreshBrowserContent: Observable<any> = this.refreshBrowserContentSubject.asObservable();

  /**
  * Get list of supported feature flags
  * @type {Observable<any>}
  */
  featureFlags: Observable<FeatureFlagModel[]> = this.featureFlagsSubject.asObservable();


  /**
   * Run to load data from the server.
   * Should be run at app launch or at other times if deemed useful.
   * @returns {Promise<void>}
   */
  async load() {
    const {
      settings,
      user,
      geoRegions,
      contactLocales,
      contactCountries,
      contactLanguages
    } = await this.http.get<AppSettingsResponseModel>("/api/settings")
      .pipe(map(it => new AppSettingsResponseModel(it)))
      .toPromise();
    this.userSubject.next(user);
    this.settingsSubject.next(settings);
    this.geoRegionsSubject.next(geoRegions);
    this.contactLocalesSubject.next(contactLocales);
    this.contactCountriesSubject.next(contactCountries);
    this.contactLanguagesSubject.next(contactLanguages);

  }

  async loadCountriesRegions() {
    const {
      countries,
      regions,
    } = await this.http.get<AppSettingsResponseModel>("/api/settings/countriesRegions")
      .pipe(map(it => new AppSettingsResponseModel(it)))
      .toPromise();   
    this.countriesSubject.next(countries);
    this.regionsSubject.next(regions);
    this.regionsByCountrySubject.next(regionsByCountry(regions));
  }

  getFeaturePermission(feature: string, projectId: string = ""): Promise<boolean> {
    if (!feature) throw new Error("No Feature provided.");

    let featureFlags;
    this.featureFlagsSubject.subscribe(flags => featureFlags = flags);
    const flag = featureFlags.find(f => f.hasOwnProperty(feature));
    if (flag) {
      return Promise.resolve(flag[feature]);
    }
    const params = {feature, projectId};

    return this.http.get<boolean>("/api/settings/feature-permission", {params}).toPromise()
    .then(apiResult => {
      const updatedFeatureFlags = [...featureFlags, { [feature]: apiResult }];
      this.featureFlagsSubject.next(updatedFeatureFlags);
      return apiResult;
    });
  }

  getFeaturePermissionsList(features: string[]): Promise<{ [key: string]: boolean }> {
    const result: { [key: string]: boolean } = {};
    const missingFeatures: string[] = [];

    let featureFlags;
    this.featureFlagsSubject.subscribe(flags => featureFlags = flags);
    // Check if the feature values are available in featureFlags
    features.forEach(feature => {
      const flag = featureFlags.find(f => f.hasOwnProperty(feature));
      if (flag) {
        result[feature] = flag[feature];
      } else {
        missingFeatures.push(feature);
      }
    });

    // If all features are found in featureFlags, return the result
    if (missingFeatures.length === 0) {
      return Promise.resolve(result);
    }

    // Call the API to get the feature values for missing features
    const params = {features: missingFeatures};
    return this.http.get<{ [key: string]: boolean }>('/api/settings/feature-permissions', {params})
      .toPromise()
      .then(apiResult => {
        // Merge the API result with the existing result
        missingFeatures.forEach(feature => {
          const value = apiResult[feature] || false;
          result[feature] = value;
          // Add the new feature to featureFlags
          const updatedFeatureFlags = [...featureFlags, { [feature]: value }];
          this.featureFlagsSubject.next(updatedFeatureFlags);
        });
        return result;
      })
      .catch(error => {
        console.error('Error fetching feature permissions:', error);
        // Return the result with false for missing features in case of an error
        missingFeatures.forEach(feature => {
          result[feature] = false;
        });
        return result;
      });
  }

  getDeployementCountry(): Observable<any> {
    return this.http.get("/api/settings/deployementCountries");
  }

  async getContactLocalCountry(){
    return await this.http.get(`/api/contact/country`);
  }
  constructor(private http: HttpClient) {}

  refreshBrowser(value: boolean = null) {
    this.refreshBrowserContentSubject.next(value);
  }
}
